import React, { useContext, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { useLogPicksMutation } from 'shared/hooks/warehouse/useLogPicksMutation';
import { useUnpickedMispickItemsQuery } from 'shared/hooks/warehouse/useUnpickedMispickItemsQuery';

import { buildLogPicksPayload, parseShipmentIdInput } from './mispickDecisions';
import { MispickShipmentLookup } from './MispickShipmentLookup';
import { MispickUnpickedItems } from './MispickUnpickedItems';

function MispickHandling() {
    const { getFacilityID, user } = useContext(UserContext);
    const facilityId = getFacilityID?.() ?? user?.facilityID;
    const [shipmentIdInput, setShipmentIdInput] = useState('');
    const [activeShipmentId, setActiveShipmentId] = useState(null);
    const [decisions, setDecisions] = useState({});
    const [decisionsKey, setDecisionsKey] = useState(null);
    const [hasSubmittedCurrentList, setHasSubmittedCurrentList] = useState(false);

    const { data, error, isError, isFetching, isSuccess, refetch } = useUnpickedMispickItemsQuery({
        shipmentId: activeShipmentId,
        facilityId,
    });

    const logPicksMutation = useLogPicksMutation({
        onSuccess: () => {
            toast.success('Decisions submitted successfully');
        },
        onError: (err) => {
            toast.error(err?.message ?? 'Failed to submit decisions');
        },
    });

    const unpicked = data?.unpicked ?? [];
    const nextDecisionsKey = activeShipmentId
        ? `${activeShipmentId}:${unpicked.map((item) => item.sellableItemId).join(',')}`
        : null;

    if (nextDecisionsKey !== decisionsKey) {
        setDecisionsKey(nextDecisionsKey);
        setDecisions({});
        setHasSubmittedCurrentList(false);
    }

    const allItemsDecided = unpicked.length > 0 && unpicked.every((item) => Boolean(decisions[item.sellableItemId]));
    const canSubmit = allItemsDecided && !hasSubmittedCurrentList && !logPicksMutation.isPending;

    const handleLookUp = (event) => {
        event.preventDefault();
        const result = parseShipmentIdInput(shipmentIdInput);
        if ('error' in result) {
            toast.error(result.error);
            return;
        }
        if (result.shipmentId === activeShipmentId) {
            setDecisions({});
            setHasSubmittedCurrentList(false);
            refetch().catch(() => {});
            return;
        }
        setActiveShipmentId(result.shipmentId);
    };

    const handleDecisionChange = (sellableItemId, value) => {
        setDecisions((prev) => ({ ...prev, [sellableItemId]: value }));
    };

    const handleSubmit = async () => {
        if (!data || !activeShipmentId || !canSubmit) {
            return;
        }

        const payload = buildLogPicksPayload({
            shipmentId: activeShipmentId,
            shipmentData: data,
            decisions,
            pickerId: user?.id ?? 0,
        });

        try {
            await logPicksMutation.mutateAsync(payload);
            setHasSubmittedCurrentList(true);
        } catch {
            // Error toast is handled by the mutation onError callback.
        }
    };

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-6">
            <div className="space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight">Shipment Mispick Handling</h1>
                <p className="text-sm text-muted-foreground">
                    Look up a shipment with a prior mispick end-of-line event, then mark each missing item as picked or
                    not picked (out of stock).
                </p>
            </div>

            <MispickShipmentLookup
                shipmentIdInput={shipmentIdInput}
                onShipmentIdInputChange={setShipmentIdInput}
                onSubmit={handleLookUp}
                isLookingUp={Boolean(activeShipmentId && isFetching)}
            />

            {activeShipmentId && isFetching && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                    Loading unpicked items…
                </div>
            )}

            {activeShipmentId && isError && (
                <Alert variant="destructive">
                    <AlertTitle>Unable to load shipment</AlertTitle>
                    <AlertDescription>{error?.message ?? 'Failed to load unpicked items'}</AlertDescription>
                </Alert>
            )}

            {activeShipmentId && isSuccess && data && (
                <MispickUnpickedItems
                    shipmentData={data}
                    unpicked={unpicked}
                    decisions={decisions}
                    onDecisionChange={handleDecisionChange}
                    onSubmit={handleSubmit}
                    canSubmit={canSubmit}
                    isSubmitting={logPicksMutation.isPending}
                />
            )}
        </div>
    );
}

export { MispickHandling };
